import React, { useCallback, useState } from 'react';

import PropTypes from 'prop-types';
import cn from 'classnames';
import { InfoBlock } from '@picsio/ui';
import { Warning } from '@picsio/icons';

import l10n from '../../shared/strings';
import {
  DraftScopeRadioOption,
  draftScopeDialogStyles as s,
} from '../DraftScopeDialogChrome';
import withErrorBoundary from '../ErrorBoundary/withErrorBoundary';
import Icon from '../Icon';
import { EDIT_DRAFT_TEXT_STRATEGY } from './editDraftTextStrategy';

const copy = l10n.PRODUCT_INFO.draftScope.editTextDialog;

function HowDoYouWantToEditTextDialog({
  hasLive = false,
  hasDraftText = false,
  onProceed,
  onCancel,
}) {
  const [source, setSource] = useState(EDIT_DRAFT_TEXT_STRATEGY.FROM_MASTER);
  const subtitle = hasLive
    ? copy.subtitle
    : (hasDraftText ? copy.noPublishedReplaceSubtitle : copy.noPublishedBody);

  const handleProceed = useCallback(() => {
    onProceed(hasLive ? source : EDIT_DRAFT_TEXT_STRATEGY.FROM_MASTER);
  }, [hasLive, onProceed, source]);

  return (
    <div className={`simpleDialog ${s.dialog}`}>
      <div className="simpleDialogUnderlayer" />
      <div className="simpleDialogBox">
        <div className="simpleDialogHeader">
          <h2 className="simpleDialogTitle">
            {hasLive ? copy.title : copy.confirmTitle}
          </h2>
          <button
            className="simpleDialogBtnCross"
            type="button"
            data-testid="btnHowDoYouWantToEditTextClose"
            onClick={onCancel}
          >
            <Icon name="close" />
          </button>
        </div>
        <div className={`simpleDialogSubHeader ${s.subtitle}`}>
          {subtitle}
        </div>
        <If condition={hasLive || hasDraftText}>
          <div className="simpleDialogContent">
            <div className="simpleDialogContentInner">
              <If condition={hasLive}>
                <div
                  className={s.options}
                  role="radiogroup"
                  aria-label={copy.title}
                >
                  <DraftScopeRadioOption
                    value={EDIT_DRAFT_TEXT_STRATEGY.FROM_MASTER}
                    selected={source}
                    title={copy.fromMasterCardTitle}
                    description={copy.fromMasterCardBody}
                    testId="btnHowDoYouWantToEditTextFromMaster"
                    onSelect={setSource}
                  />
                  <DraftScopeRadioOption
                    value={EDIT_DRAFT_TEXT_STRATEGY.FROM_LIVE}
                    selected={source}
                    title={copy.fromPublishedCardTitle}
                    description={copy.fromPublishedCardBody}
                    testId="btnHowDoYouWantToEditTextFromLive"
                    onSelect={setSource}
                  />
                </div>
              </If>
              <If condition={hasDraftText}>
                <InfoBlock
                  className={s.warning}
                  icon={Warning}
                  iconColor="warning"
                >
                  {copy.replaceWarning}
                </InfoBlock>
              </If>
            </div>
          </div>
        </If>
        <div className={cn('simpleDialogFooter', s.footer, {
          [s.footerAfterCopy]: !hasLive && !hasDraftText,
        })}
        >
          <button
            type="button"
            className="button"
            data-testid="btnHowDoYouWantToEditTextCancel"
            onClick={onCancel}
          >
            {copy.cancel}
          </button>
          <button
            type="button"
            className="button buttonAction"
            data-testid="btnHowDoYouWantToEditTextProceed"
            onClick={handleProceed}
          >
            {hasLive ? copy.apply : copy.applyFromMaster}
          </button>
        </div>
      </div>
    </div>
  );
}

HowDoYouWantToEditTextDialog.propTypes = {
  hasLive: PropTypes.bool,
  hasDraftText: PropTypes.bool,
  onProceed: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default withErrorBoundary(HowDoYouWantToEditTextDialog, {
  className: 'errorBoundaryOverlay',
});
